import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProjectDetail } from '@/api/hooks';
import { ModelFileCard } from '@/components/ModelFileCard';
import { ProgramFilesSection } from '@/components/ProgramFilesSection';
import { DRILL_SIDES } from '@/components/ProgramFilesTabs';
import { ProjectHeader } from '@/components/ProjectHeader';
import { SimulationResultsSection } from '@/components/SimulationResultsSection';
import { Button } from '@/components/ui/button';
import { usePageContext } from '@/layout/PageContext';
import type { DrillSide } from '@/types/api';
import { isSimulationRunning } from '@/utils/simulationState';

export const ProjectDetail = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setProjectName } = usePageContext();
  const [activeDir, setActiveDir] = useState<DrillSide>('+Z');

  const { data: project, isLoading, isError } = useProjectDetail(id);
  const { t } = useLingui();

  useEffect(() => {
    if (project?.name) setProjectName(project.name);
  }, [project?.name, setProjectName]);

  useEffect(() => {
    return () => setProjectName(undefined);
  }, [setProjectName]);

  // Set the active dir tab once per project navigation, not on every background refetch.
  const initialDirSetForId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!project || !id || initialDirSetForId.current === id) return;
    const firstWithFiles = DRILL_SIDES.find((dir) => project.program_files.some((f) => f.drill_side === dir));
    if (firstWithFiles) setActiveDir(firstWithFiles);
    initialDirSetForId.current = id;
  }, [project, id]);

  if (isLoading) {
    return (
      <div className="p-2 sm:p-6 text-center text-slate-500">
        <Trans>Loading…</Trans>
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="p-2 sm:p-6 text-center">
        <p className="mb-3 text-red-600">{t`Failed to load data.`}</p>
        <Button type="button" variant="outline" onClick={() => navigate('/projects')}>
          ← <Trans>Projects</Trans>
        </Button>
      </div>
    );
  }

  const isLocked = isSimulationRunning(project.simulation_state);

  return (
    <div className="fade-in mx-auto max-w-5xl p-2 sm:p-6">
      <div className="flex flex-col gap-4">
        <ProjectHeader
          project={project}
          isLocked={isLocked}
          onDeleted={() => navigate('/projects')}
          simulationState={project.simulation_state}
          collisionCount={project.simulation_result?.total_collision_count}
          backgroundTaskId={project.simulation_result?.background_task_id}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ModelFileCard
            label={t`Target 3D model`}
            model={project.target_model}
            projectId={id ?? ''}
            fileType="target"
            confirmTitle={t`Delete target model?`}
            disabled={isLocked}
          />
          <ModelFileCard
            label={t`Blank model`}
            model={project.blank_model}
            projectId={id ?? ''}
            fileType="blank"
            confirmTitle={t`Delete blank model?`}
            disabled={isLocked}
          />
        </div>

        <ProgramFilesSection
          project={project}
          projectId={id ?? ''}
          isLocked={isLocked}
          activeDir={activeDir}
          onDirChange={setActiveDir}
        />

        {(project.simulation_state === 'complete_ok' || project.simulation_state === 'complete_collisions') && (
          <SimulationResultsSection project={project} />
        )}
      </div>
    </div>
  );
};
